/**
 * Parse Day out of days (DOOD) CSV exports (client-side).
 * People = named rows; Days = date columns (YYYY-MM-DD or similar).
 */

const NAME_ALIASES = ['name', 'person', 'crew', 'cast', 'talent'];
const ROLE_ALIASES = ['role', 'job title', 'jobtitle', 'title', 'position'];
const DEPARTMENT_ALIASES = ['department', 'dept'];
const NOTES_ALIASES = ['notes', 'note', 'comments'];

const DATE_HEADER_RE = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/;
const WORK_CODES = new Set(['SW', 'W', 'P', 'H', 'T', 'R', 'S']);

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function splitCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

function findColIndex(headers, aliases) {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((h) => aliases.includes(h));
}

function isDateHeader(header) {
  const h = String(header || '').trim();
  if (DATE_HEADER_RE.test(h)) return true;
  // Also accept bare day numbers if many consecutive numeric headers exist - handled via date scan only.
  return false;
}

function fieldAt(fields, idx) {
  if (idx < 0 || idx >= fields.length) return '';
  return (fields[idx] || '').trim();
}

function countWorkDays(fields, dayIndexes) {
  let n = 0;
  dayIndexes.forEach((idx) => {
    const code = fieldAt(fields, idx).toUpperCase();
    if (!code || code === '-' || code === '-' || code === 'N/A') return;
    if (WORK_CODES.has(code) || /^[A-Z]{1,3}$/.test(code)) n += 1;
  });
  return n;
}

/**
 * @param {string} text Raw CSV file contents
 * @returns {{
 *   people: number,
 *   days: number,
 *   rowCount: number,
 *   rows: Array<{ name: string, role: string, department: string, workDays: number }>,
 *   warnings: string[],
 * }}
 */
export function parseDayOutOfDaysCsv(text) {
  const warnings = [];
  const raw = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!raw) {
    throw new Error('The day-out-of-days file is empty.');
  }

  const lines = raw.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('The day-out-of-days CSV must include a header row and at least one person.');
  }

  const headers = splitCsvLine(lines[0]);
  const nameIdx = findColIndex(headers, NAME_ALIASES);
  const roleIdx = findColIndex(headers, ROLE_ALIASES);
  const deptIdx = findColIndex(headers, DEPARTMENT_ALIASES);
  const notesIdx = findColIndex(headers, NOTES_ALIASES);

  if (nameIdx < 0) {
    throw new Error('Could not find a Name column in this day-out-of-days CSV.');
  }

  const dayIndexes = [];
  headers.forEach((h, i) => {
    if (isDateHeader(h)) dayIndexes.push(i);
  });

  if (!dayIndexes.length) {
    // Fallback: treat every column that isn't a known meta column as a day column.
    const meta = new Set([nameIdx, roleIdx, deptIdx, notesIdx].filter((i) => i >= 0));
    headers.forEach((_, i) => {
      if (!meta.has(i)) dayIndexes.push(i);
    });
    if (dayIndexes.length) {
      warnings.push('No date headers found - counted non-meta columns as days.');
    }
  }

  if (!dayIndexes.length) {
    throw new Error('Could not find day/date columns in this day-out-of-days CSV.');
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const fields = splitCsvLine(lines[i]);
    if (!fields.some((f) => f)) continue;
    const name = fieldAt(fields, nameIdx);
    if (!name) {
      warnings.push(`Row ${i + 1}: skipped (missing name).`);
      continue;
    }
    rows.push({
      name,
      role: fieldAt(fields, roleIdx),
      department: fieldAt(fields, deptIdx),
      workDays: countWorkDays(fields, dayIndexes),
    });
  }

  if (!rows.length) {
    throw new Error('No people found in this day-out-of-days CSV.');
  }

  return {
    people: rows.length,
    days: dayIndexes.length,
    rowCount: rows.length,
    rows,
    warnings,
  };
}
